// src/components/EditCardDashboard.js
import React, { useState, useEffect } from 'react';
import { Grid, Box, useMediaQuery, useTheme } from '@mui/material';
import EditCardSidenav from './EditCardSidenav';
import AboutContainer from './aboutContainer/AboutContainer';
import LinksContainer from '../links/LinksContainer';
import LeadCaptureFormContainer from '../leadCapture/LeadCaptureFormContainer';
import FollowUpEmailContainer from '../followUpEmail/FollowUpEmailContainer';
import QRCodeContainer from '../qrCode/QRCodeContainer';
import VirtualBackgroundContainer from '../virtualbackground/VirtualBackgroundContainer';
import EmailSignatureContainer from '../emailSignature/EmailSignatureContainer';
import AccessoriesContainer from 'pages/AccessoriesContainer';
import DigitalCardPreview from 'components/DigitalCardPreview';
import QRCodePreview from '../qrCode/QRCodePreview';
import VirtualBackgroundPreview from '../virtualbackground/VirtualBackgroundPreview';
import EmailSignaturePreview from '../emailSignature/EmailSignaturePreview';
import ProductsAndServicesContainer from './productsAndServices/ProductsAndServicesContainer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCardContext } from 'contexts/CardContext';

const EditCardDashboard = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const location = useLocation();
  // Extract the section from the query parameter
  const queryParams = new URLSearchParams(location.search);
  const initialSection = queryParams.get('section') || 'about';

  // Check if this is a new card creation flow
  const isNewCard = location.pathname.includes('add-card');
  // Check if coming from accessory activation
  const isFromAccessory = location.search.includes('section=accessories');

  const [selectedItem, setSelectedItem] = useState(() => {
    if (isNewCard) return 'about';
    if (isFromAccessory) return 'accessories';
    return 'about';
  });


  const { cardId } = useParams();
  const { loadCardFromServer, isTemporaryCard } = useCardContext();

  // Load fresh card data from server when component mounts
  useEffect(() => {
    const loadFreshCardData = async () => {
      if (cardId && !isTemporaryCard(cardId)) {
        try {
          console.log('🔍 EditCardDashboard: Loading fresh card data from server');
          await loadCardFromServer(cardId);
        } catch (error) {
          console.error('🔍 EditCardDashboard: Error loading fresh card data:', error);
        }
      }
    };

    loadFreshCardData();
  }, [cardId, isTemporaryCard]); // ✅ REMOVED loadCardFromServer from dependencies

  // Handle URL management
  useEffect(() => {
    // Only manage URL with search params for accessory activation
    if (isFromAccessory) {
      const queryParams = new URLSearchParams(location.search);
      const section = queryParams.get('section');
      setSelectedItem(section || 'accessories');
    }
  }, [location.search, isFromAccessory]);



  const handleMenuItemClick = (item) => {
    setSelectedItem(item);
    // Keep URLs clean - only use search params for accessories
    if (isFromAccessory) {
      navigate(`/dashboard/mycards/edit/${cardId}?section=${item}`);
    } else if (isNewCard) {
      navigate(`/dashboard/mycards/add-card/${cardId}`);
    } else {
      navigate(`/dashboard/mycards/edit/${cardId}`);
    }
  };

  // Render only specific menu items for new card
  const renderSidenav = () => {
    return (
      <EditCardSidenav
        onMenuItemClick={handleMenuItemClick}
        restrictedMode={isNewCard}
        hideOtherSections={isNewCard}
      />
    );
  };


  // Determine what to render based on the selected item
  const renderMainContent = () => {
    return (
      <>
        {/* Always render AboutContainer but conditionally show/hide */}
        <div style={{ display: selectedItem === 'about' ? 'block' : 'none' }}>
          <AboutContainer user={user} setSelectedItem={setSelectedItem} />
        </div>

        {/* Render other components conditionally */}
        {selectedItem === 'links' && <LinksContainer />}
        {selectedItem === 'productsAndServices' && <ProductsAndServicesContainer />}
        {selectedItem === 'leadCaptureForm' && <LeadCaptureFormContainer />}
        {selectedItem === 'followUpEmail' && <FollowUpEmailContainer />}
        {selectedItem === 'qrCode' && <QRCodeContainer />}
        {selectedItem === 'virtualBackground' && <VirtualBackgroundContainer />}
        {selectedItem === 'emailSignature' && <EmailSignatureContainer />}
        {selectedItem === 'accessories' && <AccessoriesContainer />}
      </>
    );
  };

  const renderPreview = () => {
    return (
      <>
        {/* Always render all preview components but conditionally show/hide */}
        <div style={{ display: selectedItem === 'qrCode' ? 'block' : 'none' }}>
          <QRCodePreview />
        </div>

        <div style={{ display: selectedItem === 'virtualBackground' ? 'block' : 'none' }}>
          <VirtualBackgroundPreview />
        </div>

        <div style={{ display: selectedItem === 'emailSignature' ? 'block' : 'none' }}>
          <EmailSignaturePreview />
        </div>

        {/* DigitalCardPreview shows for all other menu items */}
        <div style={{ display: !['qrCode', 'virtualBackground', 'emailSignature'].includes(selectedItem) ? 'block' : 'none' }}>
          <DigitalCardPreview cardId={cardId} />
        </div>
      </>
    );
  };

  return (
    <Grid container sx={{ height: '100vh', padding: 1, overflow: 'hidden' }}>
      <Grid
        item
        xs={12}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        {/* Sidebar - Further reduced width */}
        <Grid
          item
          xs={12}
          sm={2.5}
          md={2}
          sx={{
            borderRight: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: 1.5,
            borderRadius: '8px 0 0 8px',
          }}
        >
          <EditCardSidenav onMenuItemClick={handleMenuItemClick} />
        </Grid>

        {/* Main content area - Further increased width */}
        <Grid
          item
          xs={12}
          sm={9.5}
          md={9}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: 1.5,
            backgroundColor: '#ffffff',
            borderRadius: '0',
            height: 'calc(100vh - 2 * 16px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            scrollbarWidth: 'thin',
          }}
        >
          <Box sx={{ flex: 1 }}>
            {renderMainContent()}
          </Box>
        </Grid>

        {/* Right-side card preview - Kept same width */}
        <Grid
          item
          xs={12}
          sm={6}
          md={5}
          sx={{
            padding: 1.5,
            backgroundColor: '#ffffff',
            borderRadius: '0 8px 8px 0',
            borderLeft: '1px solid #e0e0e0',
            height: '100%',
          }}
        >
          {renderPreview()}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EditCardDashboard;